import React, { useRef, useEffect, useState } from 'react'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  autoFocus?: boolean
  onSave?: () => void
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = "テキストを入力してください...",
  className = "",
  autoFocus = false,
  onSave
}: RichTextEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  // 自動リサイズ機能
  const adjustHeight = () => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }

  useEffect(() => {
    adjustHeight()
  }, [value])

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [autoFocus])

  // キーボードショートカット
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl+S または Cmd+S で保存
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault()
      onSave?.()
    }

    // Tab キーでインデント
    if (e.key === 'Tab') {
      e.preventDefault()
      const textarea = textareaRef.current
      if (textarea) {
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const newValue = value.substring(0, start) + '  ' + value.substring(end)
        onChange(newValue)
        
        // カーソル位置を調整
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 2
        }, 0)
      }
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value)
  }

  return (
    <div className={`relative ${className}`}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        className={`
          w-full p-4 border rounded-lg resize-y
          transition-all duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          ${isFocused ? 'border-blue-300 shadow-lg' : 'border-gray-300 hover:border-gray-400'}
          font-mono text-sm leading-relaxed
          placeholder-gray-400
          overflow-hidden
        `}
        style={{
          lineHeight: '1.7',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
        }}
      />
      
      {/* フォーカス時のヘルプテキスト */}
      {isFocused && (
        <div className="absolute -bottom-6 left-0 text-xs text-gray-500 flex items-center gap-4">
          <span>Ctrl+S で保存</span>
          <span>Tab でインデント</span>
        </div>
      )}
    </div>
  )
}
